import jsQR from "jsqr";

// Camera QR scanning: native BarcodeDetector where available (Chromium),
// jsQR canvas fallback elsewhere (Safari). Each decoded text is delivered
// once; the caller stops the scanner when it has what it needs.

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats: string[] }) => BarcodeDetectorLike;
  }
}

export interface ScannerHandle {
  stop(): void;
}

export async function startScanner(
  video: HTMLVideoElement,
  onText: (text: string) => void,
): Promise<ScannerHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: "environment" },
    audio: false,
  });
  video.srcObject = stream;
  video.setAttribute("playsinline", "true"); // iOS: do not fullscreen the preview
  await video.play();

  const detector = window.BarcodeDetector ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const seen = new Set<string>();
  let stopped = false;

  const deliver = (text: string) => {
    if (text !== "" && !seen.has(text)) {
      seen.add(text);
      onText(text);
    }
  };

  const tick = async () => {
    if (stopped) return;
    if (video.readyState >= video.HAVE_CURRENT_DATA) {
      try {
        if (detector) {
          for (const code of await detector.detect(video)) deliver(code.rawValue);
        } else if (ctx) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(image.data, image.width, image.height);
          if (code) deliver(code.data);
        }
      } catch {
        // transient decode errors are expected between frames
      }
    }
    if (!stopped) requestAnimationFrame(() => void tick());
  };
  requestAnimationFrame(() => void tick());

  return {
    stop() {
      stopped = true;
      for (const track of stream.getTracks()) track.stop();
      video.srcObject = null;
    },
  };
}
